import React, { useState, useRef, useLayoutEffect } from "react";
import styled from "styled-components";
import { Tooltip } from "@mui/material";

/**
 * Shows as many category tags as fit in the cell; overflow count as "+n" with hover tooltip listing names.
 * `singleLine`: one row of tags (table lists); default allows up to ~2 lines (detail drawer).
 */
function CategoryTagsCell({ categories, singleLine = false }) {
  const containerRef = useRef(null);
  const lastWidthRef = useRef(-1);
  const [visibleCount, setVisibleCount] = useState((categories || []).length);
  const [resizeTick, setResizeTick] = useState(0);

  useLayoutEffect(() => {
    setVisibleCount((categories || []).length);
  }, [categories]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !categories?.length) return;
    const maxH = el.clientHeight;
    const scrollH = el.scrollHeight;
    if (scrollH > maxH) {
      setVisibleCount((prev) => (prev > 0 ? prev - 1 : prev));
    }
  }, [categories?.length, visibleCount, resizeTick]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !categories?.length) return;
    const w = el.offsetWidth;
    if (w > lastWidthRef.current) {
      lastWidthRef.current = w;
      setVisibleCount(categories.length);
    }
    const ro = new ResizeObserver(() => {
      if (!el) return;
      const newW = el.offsetWidth;
      if (newW > lastWidthRef.current) {
        lastWidthRef.current = newW;
        setVisibleCount(categories.length);
      } else {
        lastWidthRef.current = newW;
        setResizeTick((t) => t + 1);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [categories?.length]);

  const visible = (categories || []).slice(0, visibleCount);
  const remaining = (categories || []).slice(visibleCount);

  return (
    <CategoriesCell ref={containerRef} $singleLine={singleLine}>
      {visible.map((cat) => (
        <CategoryTag key={cat.id}>{cat.name}</CategoryTag>
      ))}
      {remaining.length > 0 && (
        <Tooltip title={remaining.map((c) => c.name).join(", ")} placement="top">
          <CategoryTag as="span">+{remaining.length}</CategoryTag>
        </Tooltip>
      )}
    </CategoriesCell>
  );
}

const CategoriesCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  max-height: ${({ $singleLine }) => ($singleLine ? "2em" : "3.5em")};
  overflow: hidden;
  align-content: flex-start;
`;

const CategoryTag = styled.span`
  display: inline-block;
  background: var(--surface-2);
  border-radius: 10px;
  border: 2px solid var(--border);
  padding: 1px 7px;
  font-size: 0.85em;
  margin-right: 2px;
  margin-bottom: 0px;
  white-space: nowrap;
  color: var(--text);
`;

export default CategoryTagsCell;
